import { DataResult, Result, ToxnotServiceClient } from "../../../store/ToxnotServiceClient";
import SaveDataManager from "../../../utilities/SaveDataManager";
import Layout from "../../Layout";
import { LoadingSpinnerSize } from "../LoadingSpinner";
import { LoadingSpinner } from "../LoadingSpinner";
import { ToxnotComponentProperties } from "./ToxnotComponent";
import { ToxnotSignInRequiredComponent } from "./ToxnotSignInRequiredComponent";

export enum BaseUiState {
    Loading,
    Failed
}

export interface MainApplicationViewState<TUiState> {
    uiState: BaseUiState | TUiState;
}

export interface ViewOptions {
    renderAppLayout: boolean;
}

export abstract class ToxnotMainApplicationViewComponentReadOnly<
    P extends ToxnotComponentProperties,
    TUiState,
    S extends MainApplicationViewState<TUiState>,
    SS = any
    >
    extends ToxnotSignInRequiredComponent<P, S, SS> {

    protected readonly _serviceClient: ToxnotServiceClient;

    protected constructor(props: P) {
        super(props);

        this._serviceClient = new ToxnotServiceClient();
    }

    // ===============================================================================

    private getDefaultViewOptions(): ViewOptions {
        return {
            renderAppLayout: true
        };
    }

    // ===============================================================================

    protected getViewOptions(): Partial<ViewOptions> {
        return {};
    }

    protected abstract getPostInitialLoadUiState(): TUiState;
    protected abstract fetchViewDataAndRefreshState(): Promise<void>;
    protected abstract getLocalizedViewTitle(): string | null;
    protected abstract renderViewContent(): JSX.Element | null;

    // ===============================================================================

    public async componentDidMount() {
        this.setState({
            uiState: BaseUiState.Loading
        });

        await this.fetchViewDataAndRefreshState();

        this.setState({
            uiState: this.getPostInitialLoadUiState()
        });
    }

    // ===============================================================================

    protected enterFailedState() {
        this.setState({
            uiState: BaseUiState.Failed
        });
    }

    // ===============================================================================

    protected renderConcreteComponent() {
        const viewOptions = {
            ...this.getDefaultViewOptions(),
            ...this.getViewOptions()
        };

        let content: JSX.Element | null = null;

        if (this.state.uiState === BaseUiState.Loading) {
            content = (
                <div className="tn-section-row">
                    <div className="tn-section-col-12">
                        <LoadingSpinner size={LoadingSpinnerSize.Large} />
                    </div>
                </div>
            );
        }
        else if (this.state.uiState === BaseUiState.Failed) {
            content = (
                <div className="tn-section-row">
                    <div className="tn-section-col-12">
                        {this.localizeString("Sorry, there was a problem accessing the service.")}
                    </div>
                </div>
            );
        }
        else {
            content = this.renderViewContent();
        }

        const localizedViewTitle = this.getLocalizedViewTitle();

        if (viewOptions.renderAppLayout) {
            return (
                <Layout options={{ pinAppContentToWindowHeight: true }}>
                    <div className="tn-section tn-react-scrollable-layout">
                        {localizedViewTitle !== null &&
                            <div className="tn-section-row tn-section-heading-row">
                                <div className="tn-section-col-12">
                                    <h1>{localizedViewTitle}</h1>
                                </div>
                            </div>
                        }
                        {content}
                    </div>
                </Layout>
            );
        }
        else {
            return (
                <div className="main-view-component">
                    {localizedViewTitle !== null &&
                        <div className="tn-section-row tn-section-heading-row">
                            <div className="tn-section-col-12">
                                <h1>{localizedViewTitle}</h1>
                            </div>
                        </div>
                    }
                    {content}
                </div>
            );
        }
    }

    // ===============================================================================

    protected apiDataFetchWasSuccessful<T>(apiResult: DataResult<T>) {
        return this.apiFetchWasSuccessful(apiResult) && apiResult.data !== null;
    }

    // ===============================================================================

    protected apiFetchWasSuccessful(apiResult: Result) {
        return apiResult.success;
    }
}

export abstract class ToxnotMainApplicationViewComponent<
    P extends ToxnotComponentProperties,
    TUiState,
    S extends MainApplicationViewState<TUiState>,
    TPersistType,
    SS = any
    >
    extends ToxnotMainApplicationViewComponentReadOnly<P, TUiState, S, SS> {

    protected readonly _saveDataManager: SaveDataManager<TPersistType>;

    protected constructor(props: P) {
        super(props);

        this._saveDataManager = new SaveDataManager(
            this.saveDataManager_saveData,
            this.props.appServices
        );
    }

    // ===============================================================================

    protected abstract saveDataToService(data: TPersistType): Promise<boolean>;

    // ===============================================================================

    protected data_needsToBeSaved = (): boolean => { return false }

    // ===============================================================================

    public async componentDidMount() {
        await super.componentDidMount();

        this._saveDataManager.initialize();
        this._saveDataManager.setOnNeedsToSave(this.data_needsToBeSaved);
        this._saveDataManager.enable();
    }

    // ===============================================================================

    private saveDataManager_saveData = async (data: TPersistType) => {
        return await this.saveDataToService(data);
    }
}
